import { useEffect, useState } from "react";
import { useNavigation } from "react-router";
import { Toaster as Sonner } from "@/components/ui/sonner";

/**
 * Where `toast` lands: one line at a time, just above the tab bar when it is
 * there — and above what floats on it while a page has it: the record card
 * (--record-bar) or its + (--create-button) — and at the bottom edge when
 * not.
 */
export function Toaster({ aboveTabBar }: { aboveTabBar: boolean }) {
  const bottom = aboveTabBar
    ? "calc(var(--tab-bar-top) + 1rem + var(--record-bar, 0rem) + var(--create-button, 0rem))"
    : "calc(1.5rem + env(safe-area-inset-bottom))";
  return <Sonner position="bottom-center" offset={{ bottom }} mobileOffset={{ bottom }} />;
}

/** How long a navigation loads before the bar shows, so a fast one never flickers. */
const LOADING_DELAY_MS = 150;

/**
 * A thin bar along the top while the next page loads (decided with Tiago 12
 * October): the old page stays where it is until the new one's data has
 * arrived, and this says something is on its way. A page with a copy in the
 * cache draws at once, and its read behind the copy shows nothing (0075). shadcn has no bar that
 * runs without a known end, so it is drawn here, in the theme's colours.
 */
export function LoadingBar() {
  const loading = useNavigation().state === "loading";
  const [shown, setShown] = useState(false);
  useEffect(() => {
    if (!loading) {
      setShown(false);
      return;
    }
    const wait = setTimeout(() => setShown(true), LOADING_DELAY_MS);
    return () => clearTimeout(wait);
  }, [loading]);
  if (!shown) return null;
  return (
    <div
      role="progressbar"
      aria-label="Loading"
      className="fixed inset-x-0 top-[env(safe-area-inset-top)] z-50 h-[3px] overflow-hidden bg-primary/20"
    >
      <div className="h-full w-1/3 animate-loading-bar bg-primary motion-reduce:w-full motion-reduce:animate-none" />
    </div>
  );
}
